import * as React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'

import { useDebouncedValue } from '#/shared/lib/hooks'

export const DEFAULT_PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 250

export interface PageRequestParams {
  q: string
  page: number
  size: number
}

export interface PageEnvelope<TItem> {
  items: TItem[]
  pages: number
  page: number
}

interface UseInfiniteSearchListOptions<TItem> {
  queryKey: ReadonlyArray<unknown>
  fetcher: (
    params: PageRequestParams,
    signal?: AbortSignal,
  ) => Promise<AxiosResponse<PageEnvelope<TItem>>>
  query: string
  pageSize?: number
  enabled?: boolean
}

export interface UseInfiniteSearchListResult<TItem> {
  items: TItem[]
  isLoading: boolean
  isFetchingMore: boolean
  hasMore: boolean
  loadMore: () => void
}

export function useInfiniteSearchList<TItem>({
  queryKey,
  fetcher,
  query,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled = true,
}: UseInfiniteSearchListOptions<TItem>): UseInfiniteSearchListResult<TItem> {
  const debouncedQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS)

  const result = useInfiniteQuery({
    queryKey: [...queryKey, { q: debouncedQuery, size: pageSize }],
    queryFn: async ({ pageParam, signal }) => {
      const response = await fetcher(
        { q: debouncedQuery, page: pageParam, size: pageSize },
        signal,
      )
      return response.data
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined,
    enabled,
  })

  const items = React.useMemo(
    () => result.data?.pages.flatMap((page) => page.items) ?? [],
    [result.data],
  )

  const loadMore = React.useCallback(() => {
    if (result.hasNextPage && !result.isFetchingNextPage)
      void result.fetchNextPage()
  }, [result])

  return {
    items,
    isLoading: enabled && result.isLoading,
    isFetchingMore: result.isFetchingNextPage,
    hasMore: !!result.hasNextPage,
    loadMore,
  }
}
